import React, { useState, useRef, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useToast } from '../../../../../../components/Toast/ToastContext';
import { disabilitySymbols } from '../../../../../../Constants/FormConstants';
import { getPreSignedUrl } from '../../../../../../Actions/FormActions';
import SymbolCard from '../../../../../../Shared/Components/SymbolCard/SymbolCard';
import SymbolOption from '../../../../../../Shared/Components/SymbolOption/SymbolOption';

const genderOptions = [
  { value: 'Female', label: 'Female (F)' },
  { value: 'Male', label: 'Male (M)' },
  { value: 'Other', label: 'Other (X)' }
];

const MedicalInformation = ({ formData, setFormData, errors = {}, setErrors, isLocked = false, formStatus = '' }) => {
  const { showToast } = useToast();
  const ageOptions = useMemo(() =>
    Array.from({ length: 100 }, (_, i) => ({
      value: (i + 1).toString(),
      label: (i + 1).toString()
    })), []);

  const weightOptions = useMemo(() =>
    Array.from({ length: 200 }, (_, i) => ({
      value: (i + 1).toString(),
      label: `${i + 1} Kg`
    })), []);

  const cmToFeetInches = (cm) => {
    const inches = cm / 2.54;
    const feet = Math.floor(inches / 12);
    const remainingInches = Math.round(inches % 12);
    return `${feet}ft ${remainingInches}"`;
  };

  const heightOptions = useMemo(() =>
    Array.from({ length: 256 }, (_, i) => {
      const cm = i + 49; // Start from 49 cm
      return {
        value: cm.toString(),
        label: `${cmToFeetInches(cm)} / ${cm} cm`
      };
    }), []);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [tempSelectedSymbols, setTempSelectedSymbols] = useState([]);
  const [symbolSearch, setSymbolSearch] = useState('');
  const [isNameEditable, setIsNameEditable] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const symbolsFieldRef = useRef(null);

  // Gender: keep dropdown selection separate from final value (which may be custom text)
  const presetGenderOptions = ['Female', 'Male', 'Other'];
  const [genderSelectedValue, setGenderSelectedValue] = useState(
    presetGenderOptions.includes(formData.gender) ? formData.gender : (formData.gender ? 'Other' : '')
  );
  const [customGender, setCustomGender] = useState(
    formData.gender && !presetGenderOptions.includes(formData.gender) ? formData.gender : ''
  );

  useEffect(() => {
    const current = formData.gender;
    if (presetGenderOptions.includes(current)) {
      setGenderSelectedValue(current);
      setCustomGender('');
    } else if (current) {
      setGenderSelectedValue('Other');
      setCustomGender(current);
    } else {
      setGenderSelectedValue('');
      setCustomGender('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.gender]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);
      const clickedOutsideTrigger = triggerRef.current && !triggerRef.current.contains(event.target);
      if (clickedOutsideDropdown && clickedOutsideTrigger) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  // Keep page from scrolling under the open picker so it stays anchored to the field.
  useEffect(() => {
    if (!isDropdownOpen) return undefined;

    const scrollRoot = document.getElementById('main-content') || document.documentElement;
    const previousOverflow = scrollRoot.style.overflow;
    const previousOverflowY = scrollRoot.style.overflowY;
    scrollRoot.style.overflow = 'hidden';
    scrollRoot.style.overflowY = 'hidden';

    return () => {
      scrollRoot.style.overflow = previousOverflow;
      scrollRoot.style.overflowY = previousOverflowY;
    };
  }, [isDropdownOpen]);

  // Auto-set disability status if certificate exists
  useEffect(() => {
    if (formData.disability?.certificate &&
      typeof formData.disability.certificate === 'object' &&
      Object.keys(formData.disability.certificate).length > 0) {
      setFormData(prev => ({
        ...prev,
        disability: {
          ...prev.disability,
          has_disability: 'yes',
          has_certificate: 'yes'
        }
      }));
    }
  }, [formData.disability?.certificate]);

  const handleSymbolSelect = (selectedSymbol) => {
    if (isLocked) return;
    const currentSymbols = isDropdownOpen ? (tempSelectedSymbols || []) : (formData.symbols || []);
    let newSymbols;

    if (currentSymbols.includes(selectedSymbol.id)) {
      // Remove symbol if already selected
      newSymbols = currentSymbols.filter(id => id !== selectedSymbol.id);
    } else if (currentSymbols.length < 6) {
      // Add symbol if under limit
      newSymbols = [...currentSymbols, selectedSymbol.id];
    } else {
      // Don't add if at limit
      return;
    }

    if (isDropdownOpen) {
      setTempSelectedSymbols(newSymbols);
    } else {
      setFormData({
        ...formData,
        symbols: newSymbols
      });
      if (Array.isArray(newSymbols) && newSymbols.length > 0 && setErrors) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next.symbols;
          return next;
        });
      }
    }
  };

  const openSymbolsDropdown = () => {
    if (isLocked) return;
    symbolsFieldRef.current?.scrollIntoView({ behavior: 'auto', block: 'nearest' });
    setTempSelectedSymbols(formData.symbols || []);
    setSymbolSearch('');
    setIsDropdownOpen(true);
  };

  const commitSymbolSelection = () => {
    setFormData({ ...formData, symbols: tempSelectedSymbols });
    if ((tempSelectedSymbols || []).length > 0 && setErrors) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.symbols;
        return next;
      });
    }
    setIsDropdownOpen(false);
  };

  const cancelSymbolSelection = () => {
    setTempSelectedSymbols(formData.symbols || []);
    setIsDropdownOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const mapMedicalErrorKey = (fieldName) => {
      switch (fieldName) {
        case 'medical_condition.name':
          return 'medical_condition_name';
        case 'medical_condition.notes':
          return 'medical_condition_notes';
        case 'medical_condition.doctor_name':
          return 'medical_condition_doctor';
        case 'medical_condition.certificate':
          return 'medical_certificate';
        case 'disability.has_disability':
          return 'has_disability';
        case 'disability.type':
          return 'disability_type';
        case 'disability.has_certificate':
          return 'has_certificate';
        case 'disability.certificate':
          return 'disability_certificate';
        default:
          return fieldName; // full_name, age, gender, weight, height, symbols, consent, terms_and_conditions
      }
    };

    const clearIfValid = (fieldName, fieldValue) => {
      if (!setErrors) return;
      const key = mapMedicalErrorKey(fieldName);
      const isBooleanField = fieldName === 'consent' || fieldName === 'terms_and_conditions';
      const valid = isBooleanField ? Boolean(fieldValue) : (fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== '');
      if (valid) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }
    };

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
      clearIfValid(name, value);
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
      clearIfValid(name, value);
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    const maxSize = type === 'disability' ? 5 * 1024 * 1024 : 20 * 1024 * 1024; // 5MB for disability, 20MB for medical
    if (file && file.size <= maxSize) {
      if (type === 'medical') {
        setFormData(prev => ({
          ...prev,
          medical_condition: {
            ...prev.medical_condition,
            certificate: file
          }
        }));
        if (setErrors) {
          setErrors((prev) => {
            const next = { ...prev };
            delete next.medical_certificate;
            return next;
          });
        }
      } else if (type === 'disability') {
        // Convert file to binary data
        setFormData(prev => ({
          ...prev,
          disability: {
            ...prev.disability,
            certificate: file
          }
        }));
        if (setErrors) {
          setErrors((prev) => {
            const next = { ...prev };
            delete next.disability_certificate;
            return next;
          });
        }
      }
    } else {
      const limit = type === 'disability' ? '5MB' : '20MB';
      showToast(`File size should be less than ${limit}`, 'error');
    }
  };

  const removeCertificate = (type) => {
    if (isLocked) return;
    if (type === 'medical') {
      setFormData(prev => ({
        ...prev,
        medical_condition: {
          ...prev.medical_condition,
          certificate: ''
        }
      }));
    } else if (type === 'disability') {
      setFormData(prev => ({
        ...prev,
        disability: {
          ...prev.disability,
          certificate: ''
        }
      }));
    }
  };

  const viewCertificate = async (type) => {
    const cert = type === 'medical' ? formData.medical_condition?.certificate : formData.disability?.certificate;
    if (!cert) return;
    try {
      if (cert instanceof File) {
        const url = URL.createObjectURL(cert);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      } else if (cert.file_key) {
        const response = await getPreSignedUrl(cert.file_key);
        if (response.success && response.data?.presigned_url) {
          window.open(response.data.presigned_url, '_blank');
        } else {
          showToast('Failed to get certificate URL', 'error');
        }
      }
    } catch (error) {
      showToast('Failed to open certificate', 'error');
    }
  };

  const getUploadedFileName = (cert) => {
    if (!cert) return '';
    if (cert instanceof File) return cert.name;
    if (cert.original_name) return cert.original_name;
    if (cert.file_name) return cert.file_name;
    return 'Uploaded file';
  };

  return (
    <div className="space-y-8">
      {/* Form instructions */}
      <div className="bg-[#E0D2EE] p-4 rounded-lg">
        <p className="text-sm text-[#753ABF]">
          <span className="font-medium">Note:</span> Fields marked with <span className="text-red-500">*</span> are mandatory and must be filled for form submission. Optional fields can be filled later.
        </p>
      </div>
      {/* First section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-bold text-black">
              Full Name<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                name="full_name"
                id="full_name"
                value={formData.full_name}
                onChange={handleChange}
                disabled={!isNameEditable || isLocked}
                className={`mt-1 block w-full rounded-md shadow-sm focus:border ${(!isNameEditable || isLocked) ? 'focus:border-[#cccccc]' : 'focus:border-[#753ABF]'} focus:ring-[#753ABF] px-4 py-2.5 pr-10 ${errors.full_name ? 'border-red-500' : `border ${(!isNameEditable || isLocked) ? 'border-[#cccccc]' : 'border-[#753ABF]'}`
                  } ${(!isNameEditable || isLocked) ? 'bg-[#eeecec] text-[#666666]' : 'bg-white text-black'}`}
                placeholder="Type your name here"
              />
              <button
                type="button"
                onClick={() => !isLocked && setIsNameEditable(!isNameEditable)}
                disabled={isLocked}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
              >
                <img src={"/Assets/Images/editFieldIcon.svg"} alt="Edit" className="w-4 h-4" />
              </button>
            </div>
            {errors.full_name && (
              <p className="mt-1 text-sm text-red-500">{errors.full_name}</p>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 lg:grid-cols-5 xl:grid-cols-5 gap-16">
        <div>
          <label className="block text-sm font-bold text-black">
            Age<span className="text-red-500">*</span>
          </label>
          <Select
            name="age"
            inputId="age"
            value={ageOptions.find(option => option.value === formData.age)}
            onChange={(selectedOption) => !isLocked && handleChange({
              target: {
                name: 'age',
                value: selectedOption.value
              }
            })}
            options={ageOptions}
            className={`mt-1 ${errors.age ? 'react-select-error' : ''}`}
            classNamePrefix="react-select"
            placeholder="Select age"
            isSearchable={true}
            isDisabled={isLocked}
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: isLocked ? '#cccccc' : (errors.age ? '#ef4444' : '#753ABF'),
                borderWidth: '1px',
                borderStyle: 'solid',
                boxShadow: 'none',
                backgroundColor: isLocked ? '#eeecec' : 'white',
                '&:hover': {
                  borderColor: isLocked ? '#cccccc' : '#753ABF',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#753ABF' : state.isFocused ? '#753ABF20' : 'transparent',
                color: state.isSelected ? 'white' : '#753ABF',
                '&:active': {
                  backgroundColor: '#753ABF40'
                }
              }),
              input: (base) => ({
                ...base,
                color: isLocked ? '#666666' : '#000000'
              }),
              singleValue: (base) => ({
                ...base,
                color: isLocked ? '#666666' : '#000000'
              })
            }}
          />
          {errors.age && (
            <p className="mt-1 text-sm text-red-500">{errors.age}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-bold text-black">
            Blood Group<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="blood_group"
            id="blood_group"
            value={formData.blood_group}
            onChange={handleChange}
            disabled={isLocked}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border border-[#753ABF] focus:ring-[#753ABF] px-4 py-2.5 text-black ${errors.blood_group ? 'border-red-500' : 'border border-[#753ABF]'} ${isLocked ? 'bg-[#eeecec] text-[#666666]' : ''}`}
            placeholder="Type your blood group"
          />
          {errors.blood_group && (
            <p className="mt-1 text-sm text-red-500">{errors.blood_group}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-bold text-black">
            Gender<span className="text-red-500">*</span>
          </label>
          <Select
            name="gender"
            inputId="gender"
            value={genderSelectedValue ? {
              value: genderSelectedValue,
              label: genderOptions.find(opt => opt.value === genderSelectedValue)?.label || genderSelectedValue
            } : null}
            onChange={(selectedOption) => {
              if (isLocked) return;
              setGenderSelectedValue(selectedOption.value);
              if (selectedOption.value === 'Other') {
                // Keep existing custom value in formData.gender (if any); otherwise clear until user types
                if (!formData.gender || presetGenderOptions.includes(formData.gender)) {
                  setFormData({ ...formData, gender: '' });
                }
              } else {
                handleChange({ target: { name: 'gender', value: selectedOption.value } });
              }
            }}
            options={genderOptions}
            className={`mt-1 ${errors.gender ? 'react-select-error' : ''}`}
            classNamePrefix="react-select"
            placeholder="Select gender"
            isDisabled={isLocked}
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: isLocked ? '#cccccc' : (errors.gender ? '#ef4444' : '#753ABF'),
                borderWidth: '1px',
                borderStyle: 'solid',
                boxShadow: 'none',
                backgroundColor: isLocked ? '#eeecec' : 'white',
                '&:hover': {
                  borderColor: isLocked ? '#cccccc' : '#753ABF',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#753ABF' : state.isFocused ? '#753ABF20' : 'transparent',
                color: state.isSelected ? 'white' : '#753ABF',
                '&:active': {
                  backgroundColor: '#753ABF40'
                }
              }),
              input: (base) => ({
                ...base,
                color: isLocked ? '#666666' : '#000000'
              }),
              singleValue: (base) => ({
                ...base,
                color: isLocked ? '#666666' : '#000000'
              })
            }}
          />
          {genderSelectedValue === 'Other' && (
            <div className="mt-3">
              <input
                type="text"
                name="gender_other"
                id="gender_other"
                value={customGender}
                onChange={(e) => {
                  if (isLocked) return;
                  setCustomGender(e.target.value);
                  handleChange({ target: { name: 'gender', value: e.target.value } });
                }}
                disabled={isLocked}
                className={`block w-full rounded-md shadow-sm focus:border ${isLocked ? 'focus:border-[#cccccc]' : 'focus:border-[#753ABF]'} focus:ring-[#753ABF] px-4 py-2.5 ${errors.gender ? 'border-red-500' : `border ${isLocked ? 'border-[#cccccc]' : 'border-[#753ABF]'}`} ${isLocked ? 'bg-[#eeecec] text-[#666666]' : 'bg-white text-black'}`}
                placeholder="Please specify"
              />
            </div>
          )}
          {errors.gender && (
            <p className="mt-1 text-sm text-red-500">{errors.gender}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-bold text-black">
            Weight (kg) <span className="text-red-500">*</span>
          </label>
          <Select
            name="weight"
            inputId="weight"
            value={weightOptions.find(option => option.value === formData.weight)}
            onChange={(selectedOption) => !isLocked && handleChange({
              target: {
                name: 'weight',
                value: selectedOption.value
              }
            })}
            options={weightOptions}
            className={`mt-1 ${errors.weight ? 'react-select-error' : ''}`}
            classNamePrefix="react-select"
            placeholder="Select weight"
            isSearchable={true}
            isDisabled={isLocked}
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: isLocked ? '#cccccc' : (errors.weight ? '#ef4444' : '#753ABF'),
                borderWidth: '1px',
                borderStyle: 'solid',
                boxShadow: 'none',
                backgroundColor: isLocked ? '#eeecec' : 'white',
                '&:hover': {
                  borderColor: isLocked ? '#cccccc' : '#753ABF',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#753ABF' : state.isFocused ? '#753ABF20' : 'transparent',
                color: state.isSelected ? 'white' : '#753ABF',
                '&:active': {
                  backgroundColor: '#753ABF40'
                }
              }),
              input: (base) => ({
                ...base,
                color: isLocked ? '#666666' : '#000000'
              }),
              singleValue: (base) => ({
                ...base,
                color: isLocked ? '#666666' : '#000000'
              })
            }}
          />
          {errors.weight && (
            <p className="mt-1 text-sm text-red-500">{errors.weight}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-bold text-black">
            Height <span className="text-red-500">*</span>
          </label>
          <Select
            name="height"
            inputId="height"
            value={heightOptions.find(option => option.value === formData.height)}
            onChange={(selectedOption) => !isLocked && handleChange({
              target: {
                name: 'height',
                value: selectedOption.value
              }
            })}
            options={heightOptions}
            className={`mt-1 ${errors.height ? 'react-select-error' : ''}`}
            classNamePrefix="react-select"
            placeholder="Select height"
            isSearchable={true}
            isDisabled={isLocked}
            styles={{
              control: (base, state) => ({
                ...base,
                borderColor: isLocked ? '#cccccc' : (errors.height ? '#ef4444' : '#753ABF'),
                borderWidth: '1px',
                borderStyle: 'solid',
                boxShadow: 'none',
                backgroundColor: isLocked ? '#eeecec' : 'white',
                '&:hover': {
                  borderColor: isLocked ? '#cccccc' : '#753ABF',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? '#753ABF' : state.isFocused ? '#753ABF20' : 'transparent',
                color: state.isSelected ? 'white' : '#753ABF',
                '&:active': {
                  backgroundColor: '#753ABF40'
                }
              }),
              input: (base) => ({
                ...base,
                color: isLocked ? '#666666' : '#000000'
              }),
              singleValue: (base) => ({
                ...base,
                color: isLocked ? '#666666' : '#000000'
              })
            }}
          />
          {errors.height && (
            <p className="mt-1 text-sm text-red-500">{errors.height}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        <div>
          <label className="block text-sm font-bold text-black">
            Medical Condition Name<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="medical_condition.name"
            id="medical_condition_name"
            value={formData.medical_condition?.name}
            onChange={handleChange}
            disabled={isLocked}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border border-[#753ABF] focus:ring-[#753ABF] px-4 py-2.5 text-black ${errors.medical_condition_name ? 'border-red-500' : 'border border-[#753ABF]'
              } ${isLocked ? 'bg-[#eeecec] text-[#666666]' : ''}`}
            placeholder='Type your medical condition name'
          />
          {errors.medical_condition_name && (
            <p className="mt-1 text-sm text-red-500">{errors.medical_condition_name}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-bold text-black">
            Medical Notes<span className="text-red-500">*</span>
          </label>
          <textarea
            name="medical_condition.notes"
            id="medical_condition_notes"
            value={formData.medical_condition?.notes}
            onChange={handleChange}
            disabled={isLocked}
            rows={3}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border border-[#753ABF] focus:ring-[#753ABF] px-4 py-2.5 text-black ${errors.medical_condition_notes ? 'border-red-500' : 'border border-[#753ABF]'
              } ${isLocked ? 'bg-[#eeecec] text-[#666666]' : ''}`}
            placeholder="Describe your medical condition and important things to remember"
          />
          {errors.medical_condition_notes && (
            <p className="mt-1 text-sm text-red-500">{errors.medical_condition_notes}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-bold text-black">
            Your Doctor<span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="medical_condition.doctor_name"
            id="medical_condition_doctor"
            value={formData.medical_condition?.doctor_name}
            onChange={handleChange}
            disabled={isLocked}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border border-[#753ABF] focus:ring-[#753ABF] px-4 py-2.5 text-black ${errors.medical_condition_doctor ? 'border-red-500' : 'border border-[#753ABF]'
              } ${isLocked ? 'bg-[#eeecec] text-[#666666]' : ''}`}
            placeholder='Type your medical doctor name'
          />
          {errors.medical_condition_doctor && (
            <p className="mt-1 text-sm text-red-500">{errors.medical_condition_doctor}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-black">
            Allergies <span className="text-gray-500">(optional)</span>
          </label>
          <input
            type="text"
            name="medical_condition.allergies"
            value={formData.medical_condition?.allergies}
            onChange={handleChange}
            disabled={isLocked}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border focus:ring-[#753ABF] px-4 py-2.5 text-black ${errors.allergies ? 'border-red-500' : 'border border-[#753ABF]'
              } ${isLocked ? 'bg-[#eeecec] text-[#666666]' : ''}`}
            placeholder="Type your allergies here"
          />
        </div>
      </div>
      {/* Medical Certificate Block */}
      <div className="md:w-2/3 lg:w-2/3 w-full pt-6">
        <div>
          <label className="block text-sm font-bold text-black">
            Medical Certificate<span className="text-red-500">*</span>
          </label>
          <span className="text-sm text-black">Your medical emergency documents should include the name of your condition, medications, and treatment management protocols in an emergency crisis on a registered licensed doctor's prescription. </span>
          <div className={`mt-1 flex justify-center px-6 pt-6 pb-6 border-2 border-dashed rounded-lg ${errors.medical_certificate ? 'border-red-500 bg-[#FFFFFF]' : formData.medical_condition?.certificate ? isLocked ? 'border border-[#CCCCCC] bg-[#EEECEC]' : 'border border-[#23BD78] bg-[#FFFFFF]' : 'border border-[#753ABF] bg-[#FFFFFF]'}`}> 
            {formData.medical_condition?.certificate ? (
              <div className="relative w-full flex flex-col items-center justify-center">
                <button
                  type="button"
                  onClick={() => removeCertificate('medical')}
                  disabled={isLocked}
                  className={`absolute -top-10 -right-10 p-2 rounded-full ${isLocked ? 'opacity-80 cursor-not-allowed bg-[#A7A5A5]' : 'cursor-pointer bg-[#23BD78]'}`}
                  aria-label="Remove file"
                >
                  <img src="/Assets/Images/WhiteCrossIcon.svg" alt="Remove" className="w-4" />
                </button>
                <div className="flex items-center justify-center">
                  {!isLocked && <img src="/Assets/Images/GreenUploadIcon.svg" alt="Uploaded" className="w-6 h-6 text-[#753ABF] mb-2" />}
                  {isLocked && <img src="/Assets/Images/GreyUploadIcon.svg" alt="Uploaded" className="w-6 h-6 text-[#753ABF] mb-2" />}
                  <span className={`pl-2 text-xl ${!isLocked ? 'text-[#12A46F]': 'text-[#666666]'} break-all text-center max-w-full`}>{getUploadedFileName(formData.medical_condition.certificate)}</span>
                </div>
                <button
                  type="button"
                  onClick={() => viewCertificate('medical')}
                  className={`mt-3 inline-flex items-center px-6 py-2 border ${!isLocked ? 'border-[#12A46F] text-[#12A46F] hover:bg-[#12A46F]/10': 'border-[#CCCCCC] text-[#666666] hover:bg-[#CCCCCC]/10'}  text-base font-medium rounded-md`}
                >
                  View file
                </button>
              </div>
            ) : (
              <div className="space-y-2 text-center w-full">
                <label className="relative flex flex-col items-center justify-center cursor-pointer">
                  <div className="flex items-normal justify-center">
                    <img src="/Assets/Images/UploadIcon.svg" alt="Upload" className="w-6 h-6 text-[#753ABF] mb-2" />
                    <span className="pl-2 text-base text-gray-700">Upload file less than 20MB</span>
                  </div>
                  <input
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".pdf"
                    id="medical_certificate"
                    onChange={(e) => !isLocked && handleFileUpload(e, 'medical')}
                    disabled={isLocked}
                  />
                </label>
                <p className="text-sm text-[#666666]">Supported file format: PDF</p>
              </div>
            )}
          </div>
          {errors.medical_certificate && (
            <p className="mt-1 text-sm text-red-500">{errors.medical_certificate}</p>
          )}
        </div>
      </div>
      {/* Disability information */}
      <div className="space-y-8">
        <div>
          <label className="block text-sm font-bold text-black">
            Is your Medical Health Condition a Disability?<span className="text-red-500">*</span>
          </label>
          <div className="mt-2 space-x-6">
            <label className="inline-flex items-center">
              <div className="relative">
                <input
                  type="radio"
                  name="disability.has_disability"
                  value="yes"
                  id="disability_has_disability_yes"
                  checked={formData.disability?.has_disability === 'yes'}
                  onChange={handleChange}
                  disabled={isLocked}
                  className={`peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 rounded-full bg-transparent flex items-center justify-center ${isLocked ? 'border-[#666666] cursor-not-allowed' : 'border-[#753ABF]'}`}
                />
                <svg
                  viewBox="0 0 24 24"
                  className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 hidden peer-checked:block ${isLocked ? 'text-[#666666]' : 'text-[#753ABF]'}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <span className="ml-2 text-sm text-gray-700">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <div className="relative">
                <input
                  type="radio"
                  name="disability.has_disability"
                  value="no"
                  id="disability_has_disability_no"
                  checked={formData.disability?.has_disability === 'no'}
                  onChange={handleChange}
                  disabled={isLocked}
                  className={`peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 rounded-full bg-transparent flex items-center justify-center ${isLocked ? 'border-[#666666] cursor-not-allowed' : 'border-[#753ABF]'}`}
                />
                <svg
                  viewBox="0 0 24 24"
                  className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 hidden peer-checked:block ${isLocked ? 'text-[#666666]' : 'text-[#753ABF]'}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <span className="ml-2 text-sm text-gray-700">No</span>
            </label>
          </div>
          {errors.has_disability && (
            <p className="mt-1 text-sm text-red-500">{errors.has_disability}</p>
          )}
        </div>

      </div>

      {formData.disability?.has_disability === 'yes' && (
        <>
          <div className='space-y-8'>
            <label className="block text-sm font-bold text-black">
              If yes, is your disability visible or invisible?
            </label>
            <div className="mt-2 space-x-6">
            <label className="inline-flex items-center">
              <div className="relative">
                <input
                  type="radio"
                  name="disability.type"
                  value="visible"
                  id="disability_type_visible"
                  checked={formData.disability?.type === 'visible'}
                  onChange={handleChange}
                  disabled={isLocked}
                  className={`peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 rounded-full bg-transparent flex items-center justify-center ${isLocked ? 'border-[#666666] cursor-not-allowed' : 'border-[#753ABF]'}`}
                />
                <svg
                  viewBox="0 0 24 24"
                  className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 hidden peer-checked:block ${isLocked ? 'text-[#666666]' : 'text-[#753ABF]'}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <span className="ml-2 text-sm text-gray-700">Visible</span>
            </label>
            <label className="inline-flex items-center">
              <div className="relative">
                <input
                  type="radio"
                  name="disability.type"
                  value="invisible"
                  id="disability_type_invisible"
                  checked={formData.disability?.type === 'invisible'}
                  onChange={handleChange}
                  disabled={isLocked}
                  className={`peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 rounded-full bg-transparent flex items-center justify-center ${isLocked ? 'border-[#666666] cursor-not-allowed' : 'border-[#753ABF]'}`}
                />
                <svg
                  viewBox="0 0 24 24"
                  className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 hidden peer-checked:block ${isLocked ? 'text-[#666666]' : 'text-[#753ABF]'}`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <span className="ml-2 text-sm text-gray-700">Invisible</span>
            </label>
          </div>
            {errors.disability_type && (
              <p className="mt-1 text-sm text-red-500">{errors.disability_type}</p>
            )}
          </div>
          <div className="space-y-8">
            <div>
              <label className="block text-sm font-bold text-black">
                Do you have a Disability Certificate?<span className="text-red-500">*</span>
              </label>
              <div className="mt-2 space-x-6">
                <label className="inline-flex items-center">
                  <div className="relative">
                    <input
                      type="radio"
                      name="disability.has_certificate"
                      value="yes"
                      id="disability_has_certificate_yes"
                      checked={formData.disability?.has_certificate === 'yes'}
                      onChange={(e) => {
                        if (isLocked) return;
                        handleChange(e);
                        if (e.target.value === 'no') {
                          setFormData(prev => ({
                            ...prev,
                            disability: {
                              ...prev.disability,
                              certificate: ''
                            }
                          }));
                        }
                      }}
                      disabled={isLocked}
                      className={`peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 rounded-full bg-transparent flex items-center justify-center ${isLocked ? 'border-[#666666] cursor-not-allowed' : 'border-[#753ABF]'}`}
                    />
                    <svg
                      viewBox="0 0 24 24"
                      className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 hidden peer-checked:block ${isLocked ? 'text-[#666666]' : 'text-[#753ABF]'}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <span className="ml-2 text-sm text-black">Yes</span>
                </label>
                <label className="inline-flex items-center">
                  <div className="relative">
                    <input
                      type="radio"
                      name="disability.has_certificate"
                      value="no"
                      id="disability_has_certificate_no"
                      checked={formData.disability?.has_certificate === 'no'}
                      onChange={(e) => {
                        if (isLocked) return;
                        handleChange(e);
                        setFormData(prev => ({
                          ...prev,
                          disability: {
                            ...prev.disability,
                            certificate: ''
                          }
                        }));
                      }}
                      disabled={isLocked}
                      className={`peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 rounded-full bg-transparent flex items-center justify-center ${isLocked ? 'border-[#666666] cursor-not-allowed' : 'border-[#753ABF]'}`}
                    />
                    <svg
                      viewBox="0 0 24 24"
                      className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 hidden peer-checked:block ${isLocked ? 'text-[#666666]' : 'text-[#753ABF]'}`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                  <span className="ml-2 text-sm text-gray-700">No</span>
                </label>
              </div>
              {errors.has_certificate && (
                <p className="mt-1 text-sm text-red-500">{errors.has_certificate}</p>
              )}
            </div>

            {formData.disability?.has_certificate === 'yes' && (
              <div className='md:w-2/3 lg:w-2/3 w-full'>
                <label className="block text-sm font-bold text-black">
                  Kindly upload your UDID card or Disability Certificate<span className="text-red-500">*</span>
                </label>
                <div className={`mt-1 flex justify-center px-6 pt-6 pb-6 border-2 border-dashed rounded-md ${errors.disability?.certificate ? 'border-red-500 bg-[#FFFFFF]' : formData.disability?.certificate ? isLocked ? 'border border-[#CCCCCC] bg-[#EEECEC]' : 'border border-[#23BD78] bg-[#FFFFFF]' : 'border border-[#753ABF] bg-[#FFFFFF]'}`}>
                  {formData.disability?.certificate ? (
                    <div className="relative w-full flex flex-col items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeCertificate('disability')}
                        disabled={isLocked}
                        className={`absolute -top-10 -right-10 p-2 rounded-full ${isLocked ? 'opacity-80 cursor-not-allowed bg-[#A7A5A5]' : 'cursor-pointer bg-[#23BD78]'}`}
                        aria-label="Remove file"
                      >
                        <img src="/Assets/Images/WhiteCrossIcon.svg" alt="Remove" className="w-4" />
                      </button>
                      <div className="flex items-center justify-center">
                        {!isLocked && <img src="/Assets/Images/GreenUploadIcon.svg" alt="Uploaded" className="w-6 h-6 text-[#753ABF] mb-2" />}
                        {isLocked && <img src="/Assets/Images/GreyUploadIcon.svg" alt="Uploaded" className="w-6 h-6 text-[#753ABF] mb-2" />}
                        <span className={`pl-2 text-xl ${!isLocked ? 'text-[#12A46F]': 'text-[#666666]'} break-all text-center max-w-full`}>{getUploadedFileName(formData.disability.certificate)}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => viewCertificate('disability')}
                        className={`mt-3 inline-flex items-center px-6 py-2 border ${!isLocked ? 'border-[#12A46F] text-[#12A46F] hover:bg-[#12A46F]/10': 'border-[#CCCCCC] text-[#666666] hover:bg-[#CCCCCC]/10'}  text-base font-medium rounded-md`}
                      >
                        View file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2 text-center w-full">
                      <label className="relative flex flex-col items-center justify-center cursor-pointer">
                        <div className="flex items-normal justify-center">
                          <img src="/Assets/Images/UploadIcon.svg" alt="Upload" className="w-6 h-6 text-[#753ABF] mb-2" />
                          <span className="pl-2 text-base text-gray-700">Upload file less than 5MB</span>
                        </div>
                        <input
                          type="file"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          accept=".pdf,.jpg,.jpeg,.png"
                          id="disability_certificate"
                          onChange={(e) => !isLocked && handleFileUpload(e, 'disability')}
                          disabled={isLocked}
                        />
                      </label>
                      <p className="text-sm text-[#666666]">Supported file format: PDF, JPG, PNG</p>
                    </div>
                  )}
                </div>
                {errors.disability_certificate && (
                  <p className="mt-1 text-sm text-red-500">{errors.disability_certificate}</p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <div className="space-y-8">
        <div>
          <label className="block text-sm font-bold text-black">
            Select up to 6 symbols for your card<span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-black mt-1">
            Choose symbols that best represent your needs. These symbols will be displayed on your card to help others understand how they can assist you.
          </p>
          <div id="symbols" className={`mt-4 ${errors.symbols ? 'border-red-500' : ''}`}>
            <p className="text-sm text-gray-600 mb-2">
              Selected: {formData.symbols?.length || 0} of 6
            </p>
            {errors.symbols && (
              <p className="text-sm text-red-500 mb-2">{errors.symbols}</p>
            )}
            {/* Desktop View */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
              {disabilitySymbols.map((symbol) => (
                <SymbolCard
                  key={symbol.id}
                  symbol={symbol}
                  selected={formData.symbols?.includes(symbol.id)}
                  onSelect={handleSymbolSelect}
                  disabled={isLocked || (!formData.symbols?.includes(symbol.id) && (formData.symbols?.length || 0) >= 6)}
                  isSubmitted={(String(formStatus).toLowerCase() !== 'saved') && isLocked}
                />
              ))}
            </div>

            {/* Mobile View */}
            <div className="md:hidden" ref={symbolsFieldRef}>
              <div className="relative">
                <button
                  type="button"
                  onClick={openSymbolsDropdown}
                  className={`relative w-full border ${isLocked ? 'border-[#cccccc]' : 'border-[#753ABF]'} rounded-md shadow-sm px-4 py-2 text-left focus:outline-none focus:ring-1 focus:ring-[#753ABF] focus:border ${isLocked ? 'focus:border-[#cccccc]' : 'focus:border-[#753ABF]'} ${isLocked ? 'bg-[#eeecec] text-[#666666] cursor-not-allowed opacity-70' : 'bg-white cursor-pointer'}`}
                  ref={triggerRef}
                >
                  <span className="block truncate text-sm">
                    {formData.symbols?.length ? `${formData.symbols.length}/6 Selected` : 'Select symbols'}
                  </span>
                  <span className="absolute top-1/2 right-0 -translate-y-1/2 flex items-center pr-2 pointer-events-none">
                    <svg className={`h-5 w-5 text-gray-400 transform transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </span>
                </button>

                {isDropdownOpen && (
                  <div
                    ref={dropdownRef}
                    className="absolute left-0 right-0 top-full mt-2 z-[101] bg-white rounded-2xl border border-[#753ABF] shadow-2xl overflow-hidden flex flex-col max-h-[min(360px,70vh)]"
                  >
                    <div className="px-4 py-3 flex items-center shrink-0">
                      <div className="relative w-full">
                        <input
                          type="text"
                          value={symbolSearch}
                          onChange={(e) => setSymbolSearch(e.target.value)}
                          placeholder="Search..."
                          className="w-full border border-[#753ABF] rounded-lg pl-10 pr-3 py-2 text-sm focus:outline-none"
                        />
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#753ABF]">
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.3-4.3" /></svg>
                        </span>
                      </div>
                      <button type="button" onClick={() => setIsDropdownOpen(false)} aria-label="Close" className="pl-2 text-[#4A2D7F]">
                        <img src="/Assets/Images/closeIcon.svg" alt="Close" className="w-10" />
                      </button>
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
                      {disabilitySymbols
                        .filter(s => s.label.toLowerCase().includes(symbolSearch.toLowerCase()))
                        .map((symbol) => (
                          <SymbolOption
                            key={symbol.id}
                            symbol={symbol}
                            selected={(tempSelectedSymbols || []).includes(symbol.id)}
                            onSelect={handleSymbolSelect}
                            disabled={isLocked || (!(tempSelectedSymbols || []).includes(symbol.id) && (tempSelectedSymbols?.length || 0) >= 6)}
                            tickIconSrc={'/Assets/Images/SymbolTick.svg'}
                          />
                        ))}
                    </div>
                    <div className="flex gap-4 px-4 py-4 border-t border-[#E9DFF6] bg-white shrink-0">
                      <button type="button" onClick={cancelSymbolSelection} className="flex-1 bg-[#E9DFF6] text-[#4A2D7F] font-semibold py-3 rounded-xl">Cancel</button>
                      <button type="button" onClick={commitSymbolSelection} className="flex-1 bg-[#4A2D7F] text-white font-semibold py-3 rounded-xl">Save</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        <div className="flex items-start">
          <div className="relative">
            <input
              type="checkbox"
              checked={Boolean(formData.consent)}
              onChange={(e) => !isLocked && handleChange({
                target: {
                  name: 'consent',
                  value: e.target.checked
                }
              })}
              disabled={isLocked}
              className={`peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 rounded-full bg-transparent flex items-center justify-center ${isLocked ? 'border-[#666666] cursor-not-allowed' : 'border-[#753ABF]'}`}
              id="consent_checkbox"
            />
            <svg
              viewBox="0 0 24 24"
              className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 hidden peer-checked:block ${isLocked ? 'text-[#666666]' : 'text-[#753ABF]'}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <label className="ml-3 block text-gray-700">
            I agree that the information provided by me is correct. The responsibility of any wrong information in my medical fields will be on me.
            <span className="text-red-500 ml-1">*</span>
          </label>
        </div>
        {errors.consent && (
          <p className="-mt-3 ml-9 text-sm text-red-500">{errors.consent}</p>
        )}

        {/* <div className="flex items-start">
          <div className="relative">
            <input
              type="checkbox"
              checked={Boolean(formData.lost_and_found)}
              onChange={(e) => !isLocked && handleChange({
                target: {
                  name: 'lost_and_found',
                  value: e.target.checked
                }
              })}
              disabled={isLocked}
              className="peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 border-[#753ABF] rounded-full bg-transparent disabled:opacity-50 flex items-center justify-center"
            />
            <svg
              viewBox="0 0 24 24"
              className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 text-[#753ABF] hidden peer-checked:block"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <label className="ml-3 block text-gray-700">
            Do you want to activate Lost & Found services? Applicable for Guardians & Parents of the card holder who will be able to locate the card using the user panel.
            <span className="text-gray-500 ml-1">(optional)</span>
          </label>
        </div> */}

        <div className="flex items-start">
          <div className="relative">
            <input
              type="checkbox"
              checked={Boolean(formData.terms_and_conditions)}
              onChange={(e) => !isLocked && handleChange({
                target: {
                  name: 'terms_and_conditions',
                  value: e.target.checked
                }
              })}
              disabled={isLocked}
              className={`peer h-5 w-5 md:h-6 md:w-6 shrink-0 appearance-none border-2 rounded-full bg-transparent flex items-center justify-center ${isLocked ? 'border-[#666666] cursor-not-allowed' : 'border-[#753ABF]'}`}
              id="terms_and_conditions_checkbox"
            />
            <svg
              viewBox="0 0 24 24"
              className={`pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-3 w-3 md:h-4 md:w-4 hidden peer-checked:block ${isLocked ? 'text-[#666666]' : 'text-[#753ABF]'}`}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <label className="ml-3 block text-gray-700">
            I agree with the <span className="text-[#753ABF] underline cursor-pointer" onClick={() => window.open('https://drive.google.com/file/d/1Ct74Q4_7-BlDhKRb0e1wm0Wb0gGIJ6Aw/view', '_blank')}>Terms & conditions</span> & <span className="text-[#753ABF] underline cursor-pointer" onClick={() => window.open('/privacy', '_blank')}>privacy policy</span>.
            <span className="text-red-500 ml-1">*</span>
          </label>
        </div>
        {errors.terms_and_conditions && (
          <p className="-mt-3 ml-9 text-sm text-red-500">{errors.terms_and_conditions}</p>
        )}
      </div>
    </div>
  );
};

export default MedicalInformation;
